/**
 * Stock risk categories (`dashboard.inventory_risk` in clientConfig): the
 * days-of-cover limits Inventory Risk, Purchase Order Analysis and the Command
 * Center bucket SKUs by.
 *
 * The server owns validation (control-tower inventory-risk.config.js) and
 * returns the applied limits with every dashboard response (`risk_bands`);
 * this file mirrors its defaults and rules so the editor can show problems
 * before saving, and maps the editor draft to the stored value.
 */
import { DEFAULT_RISK_BANDS, RISK_BAND_KEYS, riskDayRanges } from "./inventoryRiskPageDefaults";

export const INVENTORY_RISK_SETTINGS_KEY = "dashboard.inventory_risk";

export const RISK_BAND_LIMITS = { min: 1, max: 365 };

// Editable categories, worst first. Critical and At Risk drive Revenue at Risk,
// "Needs attention" and the Command Center, so they cannot be switched off.
export const RISK_BAND_ROWS = [
  { key: "critical", name: "Critical", required: true },
  { key: "at_risk", name: "At Risk", required: true },
  { key: "watch", name: "Watch", required: false },
  { key: "low", name: "Low", required: false },
];

const NAMES = Object.fromEntries(RISK_BAND_ROWS.map((row) => [row.key, row.name]));

const wholeNumber = (text) =>
  /^\d+$/.test(String(text).trim()) ? Number(String(text).trim()) : NaN;

/** Limits (null = off) → field errors, mirroring the server rules. */
const bandErrors = (bands) => {
  const errors = {};
  let previous = null;
  RISK_BAND_KEYS.forEach((key) => {
    const days = bands[key];
    if (days == null) return;
    if (!(Number.isInteger(days) && days >= RISK_BAND_LIMITS.min && days <= RISK_BAND_LIMITS.max)) {
      errors[key] = `Enter ${RISK_BAND_LIMITS.min}–${RISK_BAND_LIMITS.max} days.`;
      return;
    }
    if (previous && days <= previous.days) {
      errors[key] = `Must be more than ${NAMES[previous.key]} (${previous.days}).`;
    }
    previous = { key, days };
  });
  return errors;
};

/** The stored limits when they are usable, else the defaults (as the server reads them). */
export const bandsFromRow = (row) => {
  const bands = row?.value?.bands;
  if (!bands || row.value.schema_version !== 1) return { ...DEFAULT_RISK_BANDS };
  const value = Object.fromEntries(RISK_BAND_KEYS.map((key) => [key, bands[key] ?? null]));
  const usable =
    RISK_BAND_ROWS.every((band) => !band.required || value[band.key] != null) &&
    !Object.keys(bandErrors(value)).length;
  return usable ? value : { ...DEFAULT_RISK_BANDS };
};

/** "Critical ≤30 days · At Risk 30–45 days · Healthy >45 days". */
export const describeRiskBands = (bands) => {
  const ranges = riskDayRanges(bands);
  return [
    ...RISK_BAND_KEYS.filter((key) => ranges[key]).map((key) => `${NAMES[key]} ${ranges[key]}`),
    `Healthy ${ranges.healthy}`,
  ].join(" · ");
};

/** Optional categories that are off keep their default as the suggested value. */
export const draftFromBands = (bands) =>
  Object.fromEntries(
    RISK_BAND_KEYS.map((key) => [
      key,
      {
        enabled: bands[key] != null,
        days: String(bands[key] ?? DEFAULT_RISK_BANDS[key]),
      },
    ]),
  );

/** The draft as stored limits: { critical: 30, at_risk: 45, watch: null, low: null }. */
export const draftToBands = (draft) =>
  Object.fromEntries(
    RISK_BAND_KEYS.map((key) => [key, draft[key].enabled ? wholeNumber(draft[key].days) : null]),
  );

export const draftErrors = (draft) => bandErrors(draftToBands(draft));

export const draftToValue = (draft) => ({ schema_version: 1, bands: draftToBands(draft) });

export const sameBands = (left, right) =>
  Boolean(left && right) && RISK_BAND_KEYS.every((key) => (left[key] ?? null) === (right[key] ?? null));
